import { useContext, useState } from 'react'
import { AccessLevel, errorToString, Installer, InstallerRegistrationPayload } from '@oneethos/shared'
import { useLocalStorage } from '../hooks'
import { StorageKey } from '@oneethos/shared'
import { FaCheck, FaTrash, FaQuestionCircle, FaToggleOff, FaToggleOn, FaUserSecret, FaBan } from 'react-icons/fa'
import { toast } from 'react-toastify'
import { TbMailForward } from 'react-icons/tb'
import { MdLockReset } from 'react-icons/md'
import { Spinner, SpinnerSize } from '@fluentui/react'
import { TableCellSelect } from './forms'
import api from '../api-client'
import { ViewEmailLoginCode } from './view-email-login-code'
import { AppContext } from '../appContext'
import actions from '../actions'

const getStatus = (i: Installer) => {
  if (i.active === false) {
    return 'Inactive'
  } else if (i.emailVerified && i.phoneVerified) {
    return 'Registered'
  } else {
    return 'Pending Registration'
  }
}

type Props = {
  installer: Installer
  onChangeAccess: (access: string) => void
  onToggleAccess: () => void
  onDelete: (installer: Installer) => void
  self: InstallerRegistrationPayload
}

const formatPhone = (phone: string) => {
  if (!phone) return ''
  if (/^\d{10}$/.test(phone)) {
    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`
  }
  return phone
}

const ResetMfa = ({ installer, onComplete }) => {
  const [state, setState] = useState('')

  if (state === 'reset') {
    return null
  }

  if (state === 'resetting') {
    return <Spinner size={SpinnerSize.small} />
  }

  return <span title="reset mfa"
    className="ia-trigger"
    onClick={() => {
      setState('resetting')
      const notes = window.prompt(`Please add notes to confirm you have verified resetting multi-factor authorization for ${installer.email}.`)
      if (notes?.length > 10) {
        api.post(`/installers/${installer._id}/reset-mfa`, { notes })
          .then(() => {
            toast.success(
              `Multifactor auth has been reset for ${installer.email}; please instruct them to re-register with their new phone number`,
              { autoClose: false }
            )
            setState('reset')
            onComplete()
          })
          .catch(ex => {
            toast.error(`error resetting multi-factor auth: ${errorToString(ex)}`)
          })
      } else {
        setState('')
        toast.error(`Please add enough notes to confirm you have verified the authorization change for ${installer.email}.`)
      }
    }}>
    <MdLockReset color="#666" />
  </span>
}

const SetImpersonate = ({ installer }) => {
  const [, setImpersonate] = useLocalStorage(StorageKey.ImpersonateUserId, undefined)

  return <span
    style={{ cursor: 'pointer' }}
    className="ia-trigger"
    onClick={() => {
      setImpersonate(installer._id)
      window.location.reload()
    }}
  >
    <FaUserSecret color="#666" title="impersonate user" />
  </span>
}

const canModifyAccess = (self: InstallerRegistrationPayload, installer: Installer) => {
  if (installer.access === AccessLevel.PrimaryAdmin) {
    return false
  }

  if (self.access === AccessLevel.SuperAdmin) {
    return true
  }

  if ([AccessLevel.Support, AccessLevel.OEAdmin].includes(self.access)
    && installer.access !== AccessLevel.SuperAdmin) {
    return true
  }

  if (
    [AccessLevel.Admin, AccessLevel.PrimaryAdmin].includes(self.access) &&
    [AccessLevel.Rep, AccessLevel.Admin].includes(installer.access)
  ) {
    return true
  }

  return false
}

export const InstallerAdminRow = ({
  installer,
  onChangeAccess,
  onToggleAccess,
  self,
  onDelete
}: Props) => {
  const [sending, setSending] = useState('')
  const [_installer, setInstaller] = useState(installer)
  const { dispatch } = useContext(AppContext)

  const status = getStatus(_installer)

  const handleDeleteInstaller = () => {
    if (window.confirm(`Are you sure you want to delete ${installer.email}? This will also remove them from any assigned projects.`)) {
      api.delete(`/installers/${installer._id}`)
        .then((res) => {
          toast.success(res.message)
          onDelete(installer._id)
        })
        .catch(ex => {
          toast.error(ex.error)
        })
    }
  }

  const handleBanInstaller = () => {
    if (window.confirm(`Are you sure you want to ban ${installer.email}? This will prevent them from logging in.`)) {
      api.post(`/installers/${installer._id}/ban`)
        .then((res) => {
          toast.success(res.message)
          setInstaller({ ..._installer, isBanned: true })
          dispatch(actions.setTenant(res.tenant))
        })
        .catch(ex => {
          toast.error(ex.error || ex.message)
        })
    }
  }

  // TODO: 🙄 need to normalize on .id or ._id
  const isSelf = installer.id === self._id
  const disableEdit = !canModifyAccess(self, installer) || isSelf
  const showViewEmailCode = self.isSupport && [
    AccessLevel.Admin,
    AccessLevel.Rep,
    AccessLevel.PrimaryAdmin,
    AccessLevel.Consumer
  ].includes(installer.access)

  return (
    <tr className="align-middle">
      <td>
        <div className="email">
          {installer.email}
        </div>
        {installer.name &&
          <div style={{ fontSize: '0.8em' }} className="text-secondary">
            {installer.name}
          </div>
        }
        <div style={{ fontSize: '0.8em' }} className="text-secondary">
          {formatPhone(installer.phone)}
        </div>
      </td>
      <td>
        <div className="installer-actions">
          {showViewEmailCode ? <ViewEmailLoginCode installer={installer} /> : null}
          {(!isSelf && status === 'Registered' && self.isSupport && installer.isInstaller)
            ? <ResetMfa
              installer={installer}
              onComplete={() => setInstaller({
                ..._installer,
                emailVerified: false,
                phoneVerified: false,
              } as Installer)} />
            : null
          }
          {(installer.active !== false && self.access === 'super_admin') ? <SetImpersonate installer={installer} /> : null}
        </div>
      </td>
      <td>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <TableCellSelect
            value={installer.access || 'rep'}
            disabled={disableEdit || _installer.isBanned}
            options={[
              { value: 'rep', label: 'Representative' },
              { value: 'admin', label: 'Admin' },
              { value: 'primary_admin', label: 'Primary Admin', disabled: true },
              self.isSupport ? { value: 'integrator', label: 'Integration Admin' } : undefined,
              self.isSupport ? { value: 'support', label: 'Support' } : undefined,
              self.isSuperAdmin ? { value: 'oe_admin', label: 'OE Admin' } : undefined,
            ].filter(o => o)}
            onSelect={onChangeAccess}
          />
          {disableEdit ? (
            <span
              style={{ marginLeft: '0.5em' }}
              title="Access for this user can only be changed by contacting your administrator">
              <FaQuestionCircle color="#aaa" size={15} />
            </span>
          ) : null}
        </div>
      </td>
      <td>
        <div className="flex flex-row">
          {status}&nbsp;&nbsp;{status === 'Pending Registration' ? <>
            {sending === '' ? <span title="resend invite"
              className='pointer'
              onClick={() => {
                if (window.confirm(`Resend invitation to ${installer.email}?`)) {
                  setSending('sending')
                  api.post(`/installers/${installer._id}/resend-invite`, {})
                    .then(() => {
                      toast.success('successfully resent invite')
                      setSending('sent')
                    })
                    .catch(ex => {
                      toast.error(ex.error || ex.message)
                      setSending('')
                    })
                }
              }}>
              <TbMailForward color='#aaa' />
            </span> : null}
            {sending === 'sent' ? <FaCheck color='#aaa' /> : null}
            {sending === 'sending' ? <Spinner size={SpinnerSize.small} /> : null}
          </> : null}
        </div>
      </td>
      <td>
        {disableEdit || _installer.isBanned ? null : (
          <span className="tbl-btn" onClick={onToggleAccess}>
            {installer.active !== false ? <FaToggleOn color='green' /> : <FaToggleOff color='#ccc' />}
          </span>
        )}
      </td>
      {self.isSupport && !disableEdit ?
        <>
          <td className='text-center'>
            <FaTrash className='pointer' onClick={handleDeleteInstaller} color='#666' />
          </td>
          <td className='text-center'>
            {_installer.isBanned ?
              <span className='badge text-bg-secondary'>Banned User</span>
              : <button type='button' className='btn btn-outline-secondary btn-sm' onClick={handleBanInstaller}>Ban User</button>
            }
          </td>
        </>
        : 
        <td>{_installer.isBanned && <span className='badge text-bg-secondary'>Banned</span>}</td>
      }
      
    </tr>
  )
}
